import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CirclePause, CirclePlay } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { formatInt } from "@/lib/format";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Select } from "@/components/ui/select";
import type { WarmupStatus } from "./WarmupPage";

interface Outbox {
  waiting: number;
  sending_paused: boolean;
}

export function useOutbox() {
  return useQuery({ queryKey: ["email-outbox"], queryFn: () => api<Outbox>("/api/v1/email/outbox"), refetchInterval: 30_000, retry: false });
}

interface Pending {
  title: string;
  message: string;
  yes: string;
  path: string;
  body: Record<string, unknown>;
}

/**
 * Pausing, resuming and warm-up changes ask the server first (Brandon, 2026-11-02): the first call answers
 * confirm_required with a sentence saying what will happen; the change is sent again with confirm only after "Yes".
 */
function useConfirmedChange() {
  const qc = useQueryClient();
  const toast = useToast();
  const [pending, setPending] = useState<Pending | null>(null);
  const run = useMutation({
    mutationFn: async (v: { path: string; body: Record<string, unknown>; title: string; yes: string; confirm: boolean }) => {
      try {
        await api(v.path, { json: v.confirm ? { ...v.body, confirm: true } : v.body });
        return v.confirm ? "done" : "nothing";
      } catch (e) {
        if (!v.confirm && e instanceof ApiError && e.code === "confirm_required") {
          setPending({ title: v.title, message: e.message, yes: v.yes, path: v.path, body: v.body });
          return "ask";
        }
        throw e;
      }
    },
    onSuccess: (r) => {
      if (r === "ask") return;
      setPending(null);
      toast(r === "done" ? "Changed" : "Already set that way: nothing changed");
      void qc.invalidateQueries({ queryKey: ["email-outbox"] });
      void qc.invalidateQueries({ queryKey: ["email-warmup"] });
    },
    onError: (e) => toast(e.message, "error"),
  });
  const ask = (path: string, body: Record<string, unknown>, title: string, yes: string) => run.mutate({ path, body, title, yes, confirm: false });
  const dialog = pending && (
    <Dialog open onOpenChange={(o) => !o && setPending(null)}>
      <DialogContent>
        <DialogTitle>{pending.title}</DialogTitle>
        <DialogDescription>Nothing has changed yet.</DialogDescription>
        <p role="alert" className="rounded-md bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger-fg)]">
          {pending.message}
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setPending(null)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={run.isPending}
            onClick={() => run.mutate({ path: pending.path, body: pending.body, title: pending.title, yes: pending.yes, confirm: true })}
          >
            {pending.yes}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
  return { ask, dialog, busy: run.isPending };
}

/** The emergency stop for every Amazon SES email, with how much is waiting. askPause (⌘K "Pause all email sending")
 *  asks the server to pause as soon as the card loads: the same confirm as the button, nothing changes before "Yes". */
export function SendingCard({ askPause = false }: { askPause?: boolean }) {
  const q = useOutbox();
  const c = useConfirmedChange();
  const o = q.data;
  const asked = useRef(false);
  const { ask } = c;
  useEffect(() => {
    if (!askPause || asked.current || !o || o.sending_paused) return;
    asked.current = true;
    ask("/api/v1/email/pause", { paused: true }, "Pause all sending?", "Yes, pause sending");
  }, [askPause, o, ask]);
  if (!o) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Sending</CardTitle>
        {o.sending_paused ? <Badge variant="destructive">paused</Badge> : <Badge variant="success">on</Badge>}
      </CardHeader>
      <CardContent className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <span>
          {o.sending_paused ? "Every Amazon SES email (broadcasts, automations, one-offs) is on hold. " : ""}
          <span className="num">{formatInt(o.waiting)}</span> {o.waiting === 1 ? "message is" : "messages are"} waiting in the outbox. Receipts and sign-in emails are not affected.
        </span>
        {o.sending_paused ? (
          <Button size="sm" disabled={c.busy} onClick={() => c.ask("/api/v1/email/pause", { paused: false }, "Resume sending?", "Yes, resume sending")}>
            <CirclePlay className="size-4" /> Resume sending
          </Button>
        ) : (
          <Button size="sm" variant="outline" disabled={c.busy} onClick={() => c.ask("/api/v1/email/pause", { paused: true }, "Pause all sending?", "Yes, pause sending")}>
            <CirclePause className="size-4" /> Pause all sending
          </Button>
        )}
      </CardContent>
      {c.dialog}
    </Card>
  );
}

/** Change the warm-up stage or automatic advancing; the server says what the move means before it happens. */
export function WarmupControls({ w }: { w: WarmupStatus }) {
  const c = useConfirmedChange();
  const [stage, setStage] = useState<number>(w.next?.stage ?? w.stage);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Change the warm-up</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-2 text-sm">
        <label htmlFor="warmup-stage">Move to</label>
        <Select id="warmup-stage" className="w-auto" value={stage} onChange={(e) => setStage(Number(e.target.value))}>
          {w.plan.map((s) => (
            <option key={s.stage} value={s.stage}>
              Stage {s.stage}
              {w.active && s.stage === w.stage ? " (now)" : ""}
            </option>
          ))}
          <option value={0}>Finished (no limits){!w.active ? " (now)" : ""}</option>
        </Select>
        <Button size="sm" variant="outline" disabled={c.busy || stage === w.stage} onClick={() => c.ask("/api/v1/email/warmup", { stage }, "Change the warm-up stage?", "Yes, change the stage")}>
          Change stage…
        </Button>
        <span className="mx-1 text-[var(--color-muted-foreground)]">·</span>
        <Button
          size="sm"
          variant="outline"
          disabled={c.busy}
          onClick={() =>
            c.ask(
              "/api/v1/email/warmup",
              { auto_advance: !w.auto_advance },
              w.auto_advance ? "Turn off automatic advancing?" : "Turn on automatic advancing?",
              w.auto_advance ? "Yes, turn it off" : "Yes, turn it on",
            )
          }
        >
          {w.auto_advance ? "Stop advancing on its own…" : "Advance on its own…"}
        </Button>
      </CardContent>
      {c.dialog}
    </Card>
  );
}
